import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { Form, Formik } from 'formik'
import { useSelector } from 'react-redux'
import { useSnackbar } from 'notistack'
import { ConfirmActionModal } from 'src/components/shared/ConfirmActionModal'
import { Button } from 'src/components/shared/Button'
import { DataRow } from 'src/components/shared/DataRow'
import type { BasicSelectOption } from 'src/components/shared/form/BasicSelect'
import { FormikSelect } from 'src/components/shared/form/formik/FormikSelect'
import { SectionCard } from 'src/components/shared/SectionCard'
import {
  AdminFetchOnboardingTemplateList,
  AdminUpdatePartnerDriverTemplate,
} from 'src/constants/actionTypes'
import { InternalPermission } from 'src/constants/internal-access/internalPermissions'
import { useAsyncDispatchWithErrorToast } from 'src/hooks/useAsyncDispatchWithErrorToast'
import { useAuthSession } from 'src/hooks/useAuthSession'
import { useHasPermission } from 'src/hooks/useHasPermission'
import type { PartnerDriverDetails } from 'src/interfaces/partnerDriver'
import {
  getOnboardingTemplates,
  getOnboardingTemplatesError,
  getOnboardingTemplatesLoading,
} from 'src/selectors/onboardingTemplateList'
import { getPartnerDriverSaving } from 'src/selectors/partnerDriver'
import { formatDriverName } from 'src/utils/partnerDriver/driverName'
import { templateChangeFormValidator } from './TemplateChangeForm.validator'
import type { TemplateChangeFormValues } from './TemplateChangeForm.interface'

const INITIAL_VALUES: TemplateChangeFormValues = { collectDataTemplateId: '' }

interface Props {
  driver: PartnerDriverDetails
  onChanged(): void
}

/**
 * A dropdown of the driver's partner's templates rather than Retool's free-text ID field: the
 * backend rejects a template from another partner anyway, and pasting a UUID is error-prone.
 * Submitting the form only opens the confirmation; the confirmation performs the change.
 */
const TemplateCardComponent = ({ driver, onChanged }: Props) => {
  const snackbar = useSnackbar()
  const { isAuthenticated } = useAuthSession()
  const { asyncDispatchWithErrorToast } = useAsyncDispatchWithErrorToast()
  const templates = useSelector(getOnboardingTemplates)
  const isTemplatesLoading = useSelector(getOnboardingTemplatesLoading)
  const templatesError = useSelector(getOnboardingTemplatesError)
  const isSaving = useSelector(getPartnerDriverSaving)
  const [isConfirmOpen, setIsConfirmOpen] = useState<boolean>(false)
  const [hasRequestedTemplates, setHasRequestedTemplates] = useState<boolean>(false)
  // The template names come from GET /collect-data-templates (`collect_data_templates_view`).
  // Without it the fetch is skipped and the current template reads "—". Changing needs that list
  // for the picker AND `contractor_template_change` for the PUT (ENG-1207 part 2).
  const canViewTemplates = useHasPermission(InternalPermission.CollectDataTemplatesView)
  const canChangeTemplate = useHasPermission(InternalPermission.ContractorTemplateChange)
  const isChangeVisible = canViewTemplates && canChangeTemplate

  useEffect(() => {
    if (!isAuthenticated || !canViewTemplates || !driver.partnerId) {
      return
    }

    setHasRequestedTemplates(true)
    asyncDispatchWithErrorToast(
      AdminFetchOnboardingTemplateList.request({ partnerId: driver.partnerId }),
    )
  }, [asyncDispatchWithErrorToast, canViewTemplates, isAuthenticated, driver.partnerId])

  const currentTemplate = useMemo(() => {
    // The slice is shared with the customer Onboarding Templates tab; never read it unpermitted.
    if (!canViewTemplates) {
      return undefined
    }

    return templates.find((template) => {
      return template.id === driver.collectDataTemplateId
    })
  }, [canViewTemplates, templates, driver.collectDataTemplateId])

  const templateOptions = useMemo<BasicSelectOption[]>(() => {
    return templates
      .filter((template) => {
        return template.id !== driver.collectDataTemplateId
      })
      .map((template) => {
        return { label: template.name, value: template.id }
      })
  }, [templates, driver.collectDataTemplateId])

  const findTemplateName = useCallback(
    (templateId: string): string => {
      return (
        templates.find((template) => {
          return template.id === templateId
        })?.name ?? 'the selected template'
      )
    },
    [templates],
  )

  const handleSubmit = useCallback(() => {
    setIsConfirmOpen(true)
  }, [])

  const handleCloseConfirm = useCallback(() => {
    setIsConfirmOpen(false)
  }, [])

  const handleConfirm = useCallback(
    async (collectDataTemplateId: string, resetForm: () => void) => {
      const result = await asyncDispatchWithErrorToast<boolean>(
        AdminUpdatePartnerDriverTemplate.request({
          partnerDriverId: driver.id,
          collectDataTemplateId,
        }),
      )

      if (!result) {
        return
      }

      snackbar?.enqueueSnackbar('Template changed', { variant: 'success' })
      setIsConfirmOpen(false)
      resetForm()
      onChanged()
    },
    [asyncDispatchWithErrorToast, driver.id, snackbar, onChanged],
  )

  const driverName = formatDriverName(driver.firstName, driver.lastName) || 'this driver'
  const hasNoAlternatives =
    hasRequestedTemplates &&
    !isTemplatesLoading &&
    templatesError === null &&
    templateOptions.length === 0

  return (
    <SectionCard title="Collect-data template" dataTestId="partner-driver-template-card">
      <div>
        <DataRow title="Current template" text={currentTemplate?.name ?? '—'} />
        <DataRow
          title="Template ID"
          text={driver.collectDataTemplateId ?? '—'}
          textClassName={tw`font-mono`}
        />
      </div>
      {isChangeVisible ? (
        <Formik<TemplateChangeFormValues>
          initialValues={INITIAL_VALUES}
          validationSchema={templateChangeFormValidator}
          onSubmit={handleSubmit}
        >
          {({ values, resetForm }) => {
            return (
              <Form css={tw`flex flex-col gap-4`}>
                <FormikSelect<TemplateChangeFormValues>
                  name="collectDataTemplateId"
                  label="New template"
                  options={templateOptions}
                  placeholder="Choose a template"
                  disabled={isTemplatesLoading || templateOptions.length === 0}
                />
                {templatesError !== null ? (
                  <Typography variant="body2" css={tw`text-critical-500`}>
                    Couldn't load templates.
                  </Typography>
                ) : null}
                {hasNoAlternatives ? (
                  <Typography variant="body2" css={tw`text-shades-500`}>
                    This partner has no other templates.
                  </Typography>
                ) : null}
                <div css={tw`flex justify-end`}>
                  <Button
                    size="small"
                    color="dark"
                    type="submit"
                    css={tw`px-4`}
                    disabled={values.collectDataTemplateId === '' || isSaving}
                    data-testid="partner-driver-template-change-button"
                  >
                    Change template
                  </Button>
                </div>
                <ConfirmActionModal
                  isOpen={isConfirmOpen}
                  title="Change template?"
                  description={`Change ${driverName}'s template to ${findTemplateName(
                    values.collectDataTemplateId,
                  )}? Their steps will be migrated to the new template.`}
                  confirmLabel="Change template"
                  isSubmitting={isSaving}
                  onConfirm={() => {
                    handleConfirm(values.collectDataTemplateId, resetForm)
                  }}
                  onClose={handleCloseConfirm}
                  confirmDataTestId="partner-driver-template-confirm-button"
                />
              </Form>
            )
          }}
        </Formik>
      ) : null}
    </SectionCard>
  )
}

export const TemplateCard = memo(TemplateCardComponent)
